// safety/safety-walks/pages/EditObservationPage.tsx
// Edit Observation (Safety Coordinator / Safety Officer). Entry point is the
// "Edit" CTA on the observation detail page, which is shown for every status
// before Closed (the backend treats Closed as immutable — PATCH and the photo
// endpoints both reject it, so this page mirrors the rule with a read-only
// state). Save -> PATCH /api/safety/observations/:id (details only — status
// transitions keep their dedicated endpoints); photos upload/remove through
// the existing /photo endpoints so additional evidence can be added any time
// before close-out. Every change is audited in the Activity Timeline.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Loader2, Upload, X, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { useDropzone } from 'react-dropzone';
import { getObservation, updateObservation, uploadPhoto, removePhoto } from '../../../../utils/safety-walks-api';
import api from '../../../../utils/api';
import { Combobox } from '../../../../components/ui/combobox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';
import { StatusPill, RiskBadge, PhotoThumb, PhotoLightbox } from '../components/shared';
import type { SelectionUser } from '../components/WalkFormFields';
import { RISK_RATINGS, OBSERVATION_CATEGORIES } from '../../../../types/safety-walk.types';
import type { PhotoMeta, RiskRating, SafetyObservation } from '../../../../types/safety-walk.types';

const inputCls = 'w-full h-10 px-3 rounded-lg border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/30';

// Keep a stored category that is no longer in the list labelled and selectable,
// so editing never silently drops or mislabels it.
const categoryOptions = (current?: string) =>
    current && !OBSERVATION_CATEGORIES.includes(current)
        ? [current, ...OBSERVATION_CATEGORIES]
        : OBSERVATION_CATEGORIES;

// Form snapshot of the editable details. Server-managed fields (obsNumber,
// status, walk, audit, timestamps, corrective action) are never part of this.
interface EditFormState {
    title: string;
    category: string;
    description: string;
    riskRating: RiskRating;
    personValue: string; // combobox value: user id, or 'keep-current' for a stored
    // person no longer in the user list (see personOptions)
    company: string;
    dueDate: string;
}

const formFromObservation = (obs: SafetyObservation): EditFormState => ({
    title: obs.title || '',
    category: obs.category || '',
    description: obs.description || '',
    riskRating: obs.riskRating,
    personValue:
        obs.personResponsibleId != null
            ? String(obs.personResponsibleId)
            : obs.personResponsibleName
              ? 'keep-current'
              : '',
    company: obs.company || '',
    dueDate: obs.dueDate || '',
});

// One photo slot's editor: existing evidence grid (view/remove) + "add more"
// dropzone. Uploads are immediate — the observation already exists, so each
// file hits POST /photo?slot=... and the parent re-syncs from the response.
const PhotoEditorSection: React.FC<{
    obsId: number;
    slot: 'beforePhoto' | 'afterPhoto';
    title: string;
    photos: PhotoMeta[];
    onChanged: (observation: SafetyObservation) => void;
    onPreview: (photo: PhotoMeta) => void;
}> = ({ obsId, slot, title, photos, onChanged, onPreview }) => {
    const [uploading, setUploading] = useState(false);
    const [removingId, setRemovingId] = useState<string | null>(null);

    const uploadFiles = useCallback(
        async (acceptedFiles: File[]) => {
            for (const file of acceptedFiles) {
                if (!['image/png', 'image/jpeg'].includes(file.type)) {
                    return toast.error(`"${file.name}" — photo must be PNG or JPG`);
                }
                if (file.size > 10 * 1024 * 1024) {
                    return toast.error(`"${file.name}" — photo exceeds maximum limit of 10MB`);
                }
            }
            setUploading(true);
            try {
                let latest: SafetyObservation | null = null;
                for (const file of acceptedFiles) {
                    const res = await uploadPhoto(obsId, slot, file);
                    if (res.observation) latest = res.observation;
                }
                if (latest) onChanged(latest);
                toast.success(`${acceptedFiles.length} photo${acceptedFiles.length === 1 ? '' : 's'} added`);
            } catch (error: any) {
                toast.error(error.message || 'Upload failed');
            } finally {
                setUploading(false);
            }
        },
        [obsId, slot, onChanged]
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop: uploadFiles,
        accept: { 'image/png': ['.png'], 'image/jpeg': ['.jpg', '.jpeg'] },
        maxSize: 10 * 1024 * 1024,
        multiple: true,
        disabled: uploading,
    });

    const deletePhoto = async (photoId: string) => {
        setRemovingId(photoId);
        try {
            const res = await removePhoto(obsId, slot, photoId);
            if (res.observation) onChanged(res.observation);
            toast.success('Photo removed');
        } catch (error: any) {
            toast.error(error.message || 'Failed to remove photo');
        } finally {
            setRemovingId(null);
        }
    };

    return (
        <div className="rounded-xl border border-border bg-card p-5 mb-6">
            <h3 className="text-sm font-semibold mb-4">{title}</h3>
            {photos.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
                    {photos.map((photo) => (
                        <div key={photo.id} className="rounded-lg border border-border overflow-hidden">
                            <PhotoThumb photo={photo} onView={onPreview} className="border-0 rounded-none" />
                            <div className="flex items-center justify-between px-2 py-1.5 bg-muted/40 text-xs">
                                <span className="text-muted-foreground truncate">{photo.filename}</span>
                                <button
                                    onClick={() => deletePhoto(photo.id)}
                                    disabled={removingId === photo.id}
                                    className="inline-flex items-center gap-1 text-red-600 hover:underline shrink-0 disabled:opacity-50"
                                >
                                    {removingId === photo.id ? <Loader2 size={12} className="animate-spin" /> : <X size={12} />}
                                    Remove
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            <div
                {...getRootProps()}
                className={`w-full rounded-lg border-2 border-dashed border-border py-8 flex flex-col items-center justify-center text-muted-foreground cursor-pointer hover:bg-accent/40 ${
                    isDragActive ? 'border-primary bg-primary/5' : ''
                } ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
            >
                <input {...getInputProps()} />
                {uploading ? (
                    <Loader2 size={24} className="animate-spin mb-2" />
                ) : (
                    <Upload size={24} className="mb-2" />
                )}
                <span className="text-sm">{photos.length > 0 ? 'Add More Photos' : 'Upload Photo'}</span>
                <span className="text-xs mt-1">PNG, JPG up to 10MB — drag &amp; drop or click to browse (multiple allowed)</span>
            </div>
        </div>
    );
};

export const EditObservationPage: React.FC = () => {
    const { obsId } = useParams<{ obsId: string }>();
    const id = Number(obsId);
    const navigate = useNavigate();

    const [obs, setObs] = useState<SafetyObservation | null>(null);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState<EditFormState | null>(null);
    const [users, setUsers] = useState<SelectionUser[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [previewPhoto, setPreviewPhoto] = useState<PhotoMeta | null>(null);

    const fetchObs = useCallback(async () => {
        if (!id) return;
        setLoading(true);
        try {
            const res = await getObservation(id);
            setObs(res.observation || null);
            if (res.observation) setForm(formFromObservation(res.observation));
        } catch {
            // api util toasts (incl. the soft-deleted observation error)
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchObs();
    }, [fetchObs]);

    // Load system users for the Person Responsible combobox.
    useEffect(() => {
        api.get('/api/users/selection')
            .then((res) => {
                if (res.success && Array.isArray(res.users)) setUsers(res.users);
            })
            .catch(() => {});
    }, []);

    // Photo upload/remove responses return the observation without its walk
    // relation — merge so the walk context (back navigation) survives.
    const applyObservation = useCallback((next: SafetyObservation) => {
        setObs((prev) => (prev ? { ...prev, ...next } : next));
    }, []);

    // The responsible person may be a stored snapshot whose user is no longer
    // active — keep it selectable so editing never silently drops them.
    const personOptions = useMemo(() => {
        const opts = users.map((u) => ({ label: u.name, value: String(u.id) }));
        if (obs && obs.personResponsibleName) {
            const stored = obs.personResponsibleId != null ? String(obs.personResponsibleId) : null;
            if (stored === null) {
                opts.unshift({ label: obs.personResponsibleName, value: 'keep-current' });
            } else if (!opts.some((o) => o.value === stored)) {
                opts.unshift({ label: obs.personResponsibleName, value: stored });
            }
        }
        return opts;
    }, [users, obs]);

    const save = async () => {
        if (!obs || !form) return;
        if (!form.title.trim()) return toast.error('Observation title is required');

        const person = users.find((u) => String(u.id) === form.personValue);
        const keepingStoredPerson =
            !person && (form.personValue === 'keep-current' ||
                (obs.personResponsibleId != null && form.personValue === String(obs.personResponsibleId)));
        if (!person && !keepingStoredPerson) return toast.error('Person responsible is required');

        setSubmitting(true);
        try {
            await updateObservation(id, {
                title: form.title.trim(),
                category: form.category.trim() || undefined,
                description: form.description.trim() || undefined,
                riskRating: form.riskRating,
                personResponsibleId: person ? person.id : (obs.personResponsibleId ?? null),
                personResponsibleName: person ? person.name : (obs.personResponsibleName ?? null),
                company: form.company.trim() || undefined,
                dueDate: form.dueDate || undefined,
            });
            toast.success('Observation updated');
            navigate(`/safety/observations/${id}`);
        } catch (error: any) {
            toast.error(error.message || 'Failed to update observation');
        } finally {
            setSubmitting(false);
        }
    };

    const beforePhotos: PhotoMeta[] = obs && Array.isArray(obs.beforePhoto) ? obs.beforePhoto : [];
    const afterPhotos: PhotoMeta[] = obs && Array.isArray(obs.afterPhoto) ? obs.afterPhoto : [];

    if (loading) {
        return (
            <div className="flex items-center justify-center py-24">
                <Loader2 size={24} className="animate-spin text-muted-foreground" />
            </div>
        );
    }

    if (!obs || !form) {
        return (
            <div className="max-w-[900px] mx-auto px-4 py-16 text-center text-muted-foreground">
                Observation not found.
                <div>
                    <button
                        onClick={() => navigate('/safety/observations')}
                        className="mt-6 inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90"
                    >
                        Back to Safety Observations
                    </button>
                </div>
            </div>
        );
    }

    // Closed observations are immutable (server-enforced too) — read-only state.
    if (obs.status === 'Closed') {
        return (
            <div className="max-w-[900px] mx-auto px-4 py-16 text-center text-muted-foreground">
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-muted">
                    <Lock size={24} className="text-muted-foreground" />
                </div>
                <p className="text-base font-medium text-foreground">
                    This observation is closed and can no longer be edited.
                </p>
                <button
                    onClick={() => navigate(`/safety/observations/${id}`)}
                    className="mt-6 inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90"
                >
                    Back to Observation
                </button>
            </div>
        );
    }

    const patch = (p: Partial<EditFormState>) => setForm((f) => (f ? { ...f, ...p } : f));

    return (
        <div className="pb-10 px-0 sm:px-4 lg:px-0 max-w-[900px] mx-auto w-full">
            <button onClick={() => navigate(`/safety/observations/${id}`)} className="text-sm text-primary hover:underline mb-4 inline-flex items-center gap-1">
                <ChevronLeft size={14} /> Back to Observation
            </button>
            <div className="flex items-center gap-2 flex-wrap mb-1">
                <h1 className="text-2xl font-medium">Edit Observation</h1>
                <span className="text-sm font-semibold text-primary">{obs.obsNumber}</span>
                <RiskBadge risk={obs.riskRating} />
                <StatusPill status={obs.status} />
            </div>
            <p className="text-sm text-muted-foreground mb-6">All changes are recorded in the Activity Timeline.</p>

            {/* Observation details */}
            <div className="rounded-xl border border-border bg-card p-5 mb-6">
                <h3 className="text-sm font-semibold mb-4">Observation Details</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                            OBSERVATION TITLE <span className="text-red-500">*</span>
                        </label>
                        <input
                            value={form.title}
                            onChange={(e) => patch({ title: e.target.value })}
                            placeholder="e.g. Unsecured materials at edge"
                            className={inputCls}
                        />
                    </div>
                    <div>
                        <label className="text-xs font-medium text-muted-foreground mb-1.5 block">CATEGORY</label>
                        <Select value={form.category || undefined} onValueChange={(v) => patch({ category: v })}>
                            <SelectTrigger>
                                <SelectValue placeholder="Select category..." />
                            </SelectTrigger>
                            <SelectContent>
                                {categoryOptions(form.category).map((c) => (
                                    <SelectItem key={c} value={c}>{c}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="sm:col-span-2">
                        <label className="text-xs font-medium text-muted-foreground mb-1.5 block">DESCRIPTION</label>
                        <textarea
                            value={form.description}
                            onChange={(e) => patch({ description: e.target.value })}
                            placeholder="Describe the observation in detail..."
                            rows={3}
                            className="w-full px-3 py-2 rounded-lg border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                        />
                    </div>

                    <div>
                        <label className="text-xs font-medium text-muted-foreground mb-1.5 block">RISK RATING</label>
                        <Select value={form.riskRating} onValueChange={(v) => patch({ riskRating: v as RiskRating })}>
                            <SelectTrigger>
                                <SelectValue placeholder="Select risk rating..." />
                            </SelectTrigger>
                            <SelectContent>
                                {RISK_RATINGS.map((r) => (
                                    <SelectItem key={r} value={r}>{r}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                            PERSON RESPONSIBLE <span className="text-red-500">*</span>
                        </label>
                        <Combobox
                            options={personOptions}
                            value={form.personValue || undefined}
                            onChange={(value) => patch({ personValue: value || '' })}
                            placeholder="Select person..."
                            searchPlaceholder="Search users..."
                            emptyMessage="No users found."
                        />
                    </div>

                    <div>
                        <label className="text-xs font-medium text-muted-foreground mb-1.5 block">COMPANY / SUBCONTRACTOR</label>
                        <input
                            value={form.company}
                            onChange={(e) => patch({ company: e.target.value })}
                            placeholder="e.g. BuildRight Pty Ltd"
                            className={inputCls}
                        />
                    </div>
                    <div>
                        <label className="text-xs font-medium text-muted-foreground mb-1.5 block">DUE DATE</label>
                        <input
                            type="date"
                            value={form.dueDate}
                            onChange={(e) => patch({ dueDate: e.target.value })}
                            className={inputCls}
                        />
                    </div>
                </div>
            </div>

            {/* Additional photos — both slots stay editable until the observation closes */}
            <PhotoEditorSection
                obsId={id}
                slot="beforePhoto"
                title="Before Photos"
                photos={beforePhotos}
                onChanged={applyObservation}
                onPreview={setPreviewPhoto}
            />
            <PhotoEditorSection
                obsId={id}
                slot="afterPhoto"
                title="After Photos"
                photos={afterPhotos}
                onChanged={applyObservation}
                onPreview={setPreviewPhoto}
            />

            <div className="flex items-center justify-end gap-3">
                <button
                    onClick={() => navigate(`/safety/observations/${id}`)}
                    className="h-10 px-4 rounded-lg border border-border bg-card text-sm font-medium hover:bg-accent"
                >
                    Cancel
                </button>
                <button
                    onClick={save}
                    disabled={submitting}
                    className="inline-flex items-center gap-2 h-10 px-5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
                >
                    {submitting && <Loader2 size={15} className="animate-spin" />}
                    Save Changes
                </button>
            </div>

            {/* Photo lightbox — full-resolution view */}
            <PhotoLightbox photo={previewPhoto} onClose={() => setPreviewPhoto(null)} />
        </div>
    );
};
